import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MusicProvider } from "./context/MusicContext";
import { BillingProvider } from "./context/BillingContext";
import { FavouritesProvider } from "./context/FavouritesContext";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import WorkoutPage from "./pages/WorkoutPage";
import NutritionPage from "./pages/NutritionPage";
import ProgressPage from "./pages/ProgressPage";
import ProfilePage from "./pages/ProfilePage";
import LibraryPage from "./pages/LibraryPage";
import HistoryPage from "./pages/HistoryPage";
import SessionPage from "./pages/SessionPage";
import GoalsPage from "./pages/GoalsPage";
import OnboardingPage from "./pages/OnboardingPage";
import MeasurementsPage from "./pages/MeasurementsPage";
import MusicPage from "./pages/MusicPage";
import BillingPage from "./pages/BillingPage";
import PricingPage from "./pages/PricingPage";
import TutorialPage from "./pages/TutorialPage";
import ProgressPhotosPage from "./pages/ProgressPhotosPage";
import PrivacyPage from "./pages/PrivacyPage";
import RecoverPage from "./pages/RecoverPage";
import SupportPage from "./pages/SupportPage";
import TermsPage from "./pages/TermsPage";
import AchievementsPage from "./pages/AchievementsPage";
import PublicBadgePage from "./pages/PublicBadgePage";
import SpotifyPlayer from "./components/SpotifyPlayer";
import ThemeRouteSync from "./components/ThemeRouteSync";
import UpgradeModalListener from "./components/UpgradeModalListener";
import HelpButton from "./components/HelpButton";
import WelcomeTourModal from "./components/WelcomeTourModal";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white/60">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

/**
 * Persistent music pill mounted INSIDE the router so it can read the current
 * pathname, but OUTSIDE <Routes> so the iframe never remounts when the user
 * navigates between authenticated routes (dashboard → workout → session).
 *
 * Music is intentionally hidden on:
 *   - Landing (`/`)
 *   - Auth (`/auth`)
 *   - Onboarding (`/onboarding`)
 * It only appears on the in-app routes: `/app/*` and `/session/:idx`.
 */
const PersistentMusicPill = () => {
  const { pathname } = useLocation();
  const isInApp = pathname.startsWith("/app") || pathname.startsWith("/session");
  if (!isInApp) return null;
  return <SpotifyPlayer />;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <MusicProvider>
            <BillingProvider>
              <FavouritesProvider>
              <BrowserRouter>
            <ThemeRouteSync />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "var(--surface)",
                  color: "var(--text)",
                  border: "1px solid var(--border-color)",
                },
              }}
            />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/recover" element={<RecoverPage />} />
              <Route
                path="/onboarding"
                element={
                  <Protected>
                    <OnboardingPage />
                  </Protected>
                }
              />
              <Route
                path="/session/:dayIndex"
                element={
                  <Protected>
                    <SessionPage />
                  </Protected>
                }
              />
              <Route
                path="/app"
                element={
                  <Protected>
                    <AppLayout />
                  </Protected>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="workout" element={<WorkoutPage />} />
                <Route path="library" element={<LibraryPage />} />
                <Route path="history" element={<HistoryPage />} />
                <Route path="goals" element={<GoalsPage />} />
                <Route path="achievements" element={<AchievementsPage />} />
                <Route path="measurements" element={<MeasurementsPage />} />
                <Route path="music" element={<MusicPage />} />
                <Route path="nutrition" element={<NutritionPage />} />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="tutorial" element={<TutorialPage />} />
                <Route path="progress-photos" element={<ProgressPhotosPage />} />
              </Route>
              <Route path="/b/:type/:tier" element={<PublicBadgePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <PersistentMusicPill />
            <HelpButton />
            <WelcomeTourModal />
            <UpgradeModalListener />
          </BrowserRouter>
              </FavouritesProvider>
          </BillingProvider>
          </MusicProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
